<template>
  <main class="app-content">
    <div class="app-title">
      <h1><font-awesome-icon icon="robot"/> AI Trợ lý - Lịch sử câu hỏi</h1>
    </div>

    <!-- Filters -->
    <div class="row mb-3">
      <div class="col-md-3">
        <b-form-group label="Từ khóa">
          <b-input-group>
            <b-input-group-prepend is-text>
              <font-awesome-icon icon="search"/>
            </b-input-group-prepend>
            <b-form-input v-model="filter.keyword" placeholder="Tìm câu hỏi, câu trả lời..." @keyup.enter="load"/>
          </b-input-group>
        </b-form-group>
      </div>
      <div class="col-md-2">
        <b-form-group label="Trạng thái">
          <b-form-select v-model="filter.status" :options="statusOptions" @change="load"/>
        </b-form-group>
      </div>
      <div class="col-auto">
        <b-form-group label="Từ ngày">
          <b-form-input type="date" v-model="filter.from" style="width: 160px; cursor: pointer;" @click.native="$event.target.showPicker && $event.target.showPicker()"/>
        </b-form-group>
      </div>
      <div class="col-auto">
        <b-form-group label="Đến ngày">
          <b-form-input type="date" v-model="filter.to" style="width: 160px; cursor: pointer;" @click.native="$event.target.showPicker && $event.target.showPicker()"/>
        </b-form-group>
      </div>
      <div class="col-auto d-flex align-items-end">
        <b-button variant="primary" @click="load" class="mb-3 mr-2">
          <font-awesome-icon icon="search"/> Tìm kiếm
        </b-button>
        <b-button variant="success" @click="exportCSV" class="mb-3" :disabled="exporting">
          <font-awesome-icon icon="download"/> CSV
        </b-button>
      </div>
    </div>

    <!-- Table -->
    <b-table :items="items" :fields="fields" striped hover responsive small show-empty empty-text="Không có dữ liệu">
      <template #cell(createTime)="data">
        {{ data.value | moment('DD/MM/YYYY HH:mm') }}
      </template>
      <template #cell(client)="data">
        <span v-if="data.value && (data.value.companyName || data.value.contactName || data.value.domain)">
          {{ data.value.companyName || data.value.contactName || '—' }}
          <small v-if="data.value.domain" class="text-muted d-block">{{ data.value.domain }}</small>
          <small v-if="data.value.code" class="text-muted d-block">{{ data.value.code }}</small>
        </span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(user)="data">
        <span v-if="data.value">{{ data.value.name }} <small class="text-muted">({{ data.value.ext }})</small></span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(question)="data">
        <span :title="data.value">{{ truncate(data.value, 80) }}</span>
      </template>
      <template #cell(answer)="data">
        <span :title="data.value">{{ truncate(data.value, 80) }}</span>
      </template>
      <template #cell(status)="data">
        <b-badge :variant="statusVariant(data.value)">{{ statusLabel(data.value) }}</b-badge>
      </template>
      <template #cell(toolsUsed)="data">
        <span v-if="data.value && data.value.length">
          <b-badge v-for="t in data.value" :key="t" variant="info" class="mr-1">{{ t }}</b-badge>
        </span>
        <span v-else class="text-muted">—</span>
      </template>
      <template #cell(duration)="data">
        {{ data.value ? (data.value / 1000).toFixed(1) + 's' : '—' }}
      </template>
      <template #cell(actions)="data">
        <b-button size="sm" variant="outline-primary" @click="showDetail(data.item)">
          <font-awesome-icon icon="eye"/>
        </b-button>
      </template>
    </b-table>

    <!-- Pagination -->
    <div class="d-flex justify-content-between align-items-center" v-if="totalDocs > 0">
      <small class="text-muted">{{ totalDocs }} kết quả</small>
      <b-pagination v-model="page" :total-rows="totalDocs" :per-page="pageSize" @change="onPageChange"/>
    </div>

    <!-- Detail modal -->
    <b-modal v-model="showModal" title="Chi tiết câu hỏi" size="lg" ok-only ok-title="Đóng">
      <div v-if="detail">
        <div class="mb-3">
          <strong>Khách hàng:</strong> {{ detail.client ? (detail.client.companyName || detail.client.contactName || '—') : '—' }}
          <span v-if="detail.client && detail.client.domain" class="text-muted">({{ detail.client.domain }})</span>
          <span v-if="detail.client && detail.client.code" class="text-muted ml-1">[{{ detail.client.code }}]</span>
        </div>
        <div class="mb-3">
          <strong>Nhân viên:</strong> {{ detail.user ? detail.user.name : '—' }}
          <span v-if="detail.user" class="text-muted">({{ detail.user.ext }})</span>
        </div>
        <div class="mb-3">
          <strong>Thời gian:</strong> {{ detail.createTime | moment('DD/MM/YYYY HH:mm:ss') }}
          <span class="ml-2">
            <b-badge :variant="statusVariant(detail.status)">{{ statusLabel(detail.status) }}</b-badge>
          </span>
        </div>
        <div class="mb-3" v-if="detail.duration">
          <strong>Thời gian xử lý:</strong> {{ (detail.duration / 1000).toFixed(1) }}s
        </div>
        <div class="mb-3" v-if="detail.toolsUsed && detail.toolsUsed.length">
          <strong>Tools:</strong>
          <b-badge v-for="t in detail.toolsUsed" :key="t" variant="info" class="mr-1">{{ t }}</b-badge>
        </div>
        <div class="mb-3" v-if="detail.tokenUsage">
          <strong>Token:</strong> prompt={{ detail.tokenUsage.promptTokens }}, completion={{ detail.tokenUsage.completionTokens }}, total={{ detail.tokenUsage.totalTokens }}
        </div>

        <hr/>

        <div class="mb-3">
          <strong>Câu hỏi:</strong>
          <div class="bg-light p-2 rounded mt-1" style="white-space: pre-wrap;">{{ detail.question }}</div>
        </div>
        <div class="mb-3">
          <strong>Câu trả lời:</strong>
          <div class="bg-light p-2 rounded mt-1" style="white-space: pre-wrap;">{{ detail.answer }}</div>
        </div>
        <div class="mb-3" v-if="detail.errorMessage">
          <strong class="text-danger">Lỗi:</strong>
          <div class="bg-danger text-white p-2 rounded mt-1">{{ detail.errorMessage }}</div>
        </div>
      </div>
    </b-modal>
  </main>
</template>

<script>
export default {
  data() {
    var now = new Date();
    var sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return {
      items: [],
      page: 1,
      pageSize: 20,
      totalDocs: 0,
      filter: {
        status: '',
        keyword: '',
        from: sevenDaysAgo.toISOString().split('T')[0],
        to: now.toISOString().split('T')[0],
      },
      statusOptions: [
        {value: '', text: 'Tất cả'},
        {value: 'success', text: 'Thành công'},
        {value: 'error', text: 'Lỗi'},
        {value: 'no_tool', text: 'Không dùng tool'},
      ],
      fields: [
        {key: 'createTime', label: 'Thời gian', sortable: true, class: 'text-nowrap'},
        {key: 'client', label: 'Khách hàng'},
        {key: 'user', label: 'Nhân viên'},
        {key: 'question', label: 'Câu hỏi'},
        {key: 'status', label: 'Trạng thái', class: 'text-center'},
        {key: 'toolsUsed', label: 'Tools'},
        {key: 'duration', label: 'Thời gian', class: 'text-right'},
        {key: 'actions', label: '', class: 'text-center'},
      ],
      showModal: false,
      detail: null,
      exporting: false,
    };
  },
  methods: {
    load() {
      var params = {
        page: this.page,
        pageSize: this.pageSize,
      };
      if (this.filter.status) params.status = this.filter.status;
      if (this.filter.keyword) params.keyword = this.filter.keyword;
      if (this.filter.from) params.from = new Date(this.filter.from).getTime();
      if (this.filter.to) params.to = new Date(this.filter.to).getTime() + 24 * 60 * 60 * 1000 - 1;

      this.$service.get('/ai-assistant/logs', {params: params})
        .then(res => {
          this.items = res.data.docs;
          this.totalDocs = res.data.totalDocs;
        });
    },
    onPageChange(page) {
      this.page = page;
      this.load();
    },
    showDetail(item) {
      this.detail = item;
      this.showModal = true;
    },
    exportCSV() {
      this.exporting = true;
      var params = {};
      if (this.filter.status) params.status = this.filter.status;
      if (this.filter.keyword) params.keyword = this.filter.keyword;
      if (this.filter.from) params.from = new Date(this.filter.from).getTime();
      if (this.filter.to) params.to = new Date(this.filter.to).getTime() + 24 * 60 * 60 * 1000 - 1;

      this.$service.get('/ai-assistant/export', {params: params, responseType: 'blob'})
        .then(function (res) {
          var url = window.URL.createObjectURL(new Blob([res.data]));
          var a = document.createElement('a');
          a.href = url;
          a.download = 'ai-assistant-logs.csv';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        })
        .finally(function () {
          this.exporting = false;
        }.bind(this));
    },
    truncate(str, len) {
      if (!str) return '—';
      return str.length > len ? str.substring(0, len) + '...' : str;
    },
    statusVariant(status) {
      if (status === 'success') return 'success';
      if (status === 'error') return 'danger';
      if (status === 'no_tool') return 'warning';
      return 'secondary';
    },
    statusLabel(status) {
      if (status === 'success') return 'Thành công';
      if (status === 'error') return 'Lỗi';
      if (status === 'no_tool') return 'Không tool';
      return status;
    },
  },
  mounted() {
    this.load();
  },
};
</script>
